import { Flex } from '@bookjane2/bookjane-design-library';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import {
  CallOutHeader,
  BiddingWrapper,
  CallOutViewDetailsLink,
  CallOutTitleSection,
  CallOutBodySection,
  EstimatedTimeSection,
  BKJProgressBar,
  CallOutProgressBarSection,
  CallOutHeaderText,
  CallOutHeaderWrapper,
  ProgressBold,
  ProgressText,
  AutoExtendLabel,
  AutoExtendValue,
} from 'modals/ShiftModal/src/views/DetailView/GeneralTab/CallOut.styled';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { DateTime } from 'luxon';
import { useBiddingTime } from 'hooks/useBiddingTime';
import { AllowOvertimeDialog } from 'components/AllowOvertimeDialog';
import { AutoRebidDialog } from 'components/AutoRebidDialog';
import { DeleteBlockDialog } from 'components/DeleteBlockDialog';
import { ContinueCalloutDialog } from 'components/ContinueCalloutDialog';
import { toastService } from 'services';
import {
  ShiftAction,
  RenderShiftActionsProps,
} from 'components/RenderShiftActions/RenderShiftActions.types';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { ShiftActionsWrapper } from 'components/ShiftCardSkeleton/ShiftCardSkeleton.styled';
import { RenderShiftActions } from 'components/RenderShiftActions';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';
import { useSelector } from 'react-redux';

export const calculateCurrentProgress = (
  shiftCreationTime: string | undefined,
  biddingTimeEndsAt: string | undefined,
  timeZone: string | undefined,
): number | undefined => {
  if (!shiftCreationTime || !biddingTimeEndsAt) return undefined;

  const shiftTime = DateTime.fromISO(shiftCreationTime).setZone(timeZone);
  const biddingEndTime = DateTime.fromISO(biddingTimeEndsAt).setZone(timeZone);
  const stepValue = (biddingEndTime.valueOf() - shiftTime.valueOf()) / 10;
  const currentTime = DateTime.now().setZone(timeZone);
  const currentProgress = Math.floor((currentTime.valueOf() - shiftTime.valueOf()) / stepValue);

  return currentProgress;
};

export interface IBiddingSectionProps {
  shiftActions: ShiftAction[];
  isShiftInfoWrapperVisible: boolean;
  ShiftActionComponent: RenderShiftActionsProps['SingleItemComponent'];
  isContinueCallOutConfirmationOpen: boolean;
  isAllowOvertimeConfirmationOpen: boolean;
  isAutoRebidConfirmationOpen: boolean;
  isBlockDeleteConfirmationOpen: boolean;
  setIsContinueCallOutConfirmationOpen: Dispatch<SetStateAction<boolean>>;
  setIsAllowOvertimeConfirmationOpen: Dispatch<SetStateAction<boolean>>;
  setIsAutoRebidConfirmationOpen: Dispatch<SetStateAction<boolean>>;
  setIsBlockDeleteConfirmationOpen: Dispatch<SetStateAction<boolean>>;
}

export function BiddingSection(props: IBiddingSectionProps) {
  const { t } = useTranslation();
  const is24hTimeFormat = useSelector(isUserTimeFormat24hSelector);
  const timeFormat = is24hTimeFormat ? 'H:mm' : 'h:mm a';
  const { data, refetch } = useShiftModalDataContext();
  const order = data?.order;

  const { refreshBackgroundPage } = useShiftModalPropsContext();

  const biddingTimeEndsAt = order?.biddingTimeEndsAt ?? null;
  useBiddingTime({
    biddingTimeEndsAt,
    timeZone: order?.timeZone,
    orderRelayId: order?.relayId ?? '',
  });
  const goToAuditTrailView = useCallback(
    (calloutListId: string) =>
      shiftModalRoutingService.goToView('AuditTrailView', {
        calloutListId: calloutListId,
      }),
    [],
  );

  const timeZone = order?.timeZone?.toString();

  const currentProgress = useMemo(() => {
    const progress = calculateCurrentProgress(order?.createdAt, order?.biddingTimeEndsAt, timeZone);

    if (progress !== undefined) {
      return progress;
    }
  }, [order?.biddingTimeEndsAt, order?.createdAt, timeZone]);

  const maxStep = 10;

  const biddingStatusLabel =
    typeof currentProgress === 'number'
      ? currentProgress < maxStep
        ? t('shift_modal:BIDDING_IN_PROGRESS')
        : t('shift_modal:BIDDING_COMPLETE')
      : t('shift_modal:BIDDING_PROGRESS_NOT_AVAILABLE');

  const {
    shiftActions,
    isShiftInfoWrapperVisible,
    ShiftActionComponent,
    isContinueCallOutConfirmationOpen,
    isAllowOvertimeConfirmationOpen,
    isAutoRebidConfirmationOpen,
    isBlockDeleteConfirmationOpen,
    setIsContinueCallOutConfirmationOpen,
    setIsAllowOvertimeConfirmationOpen,
    setIsAutoRebidConfirmationOpen,
    setIsBlockDeleteConfirmationOpen,
  } = props;

  return (
    <BiddingWrapper>
      <ContinueCalloutDialog
        orderRelayId={order?.relayId}
        isContinueCallOutConfirmationOpen={isContinueCallOutConfirmationOpen}
        setIsContinueCallOutConfirmationOpen={setIsContinueCallOutConfirmationOpen}
        onSuccess={() => {
          toastService.success(t('dashboard:CALL_OUT_SUCCESSFUL_RESUMED'));
          refreshBackgroundPage();
        }}
      />
      <AllowOvertimeDialog
        orderId={order?.id}
        isAllowOvertimeConfirmationOpen={isAllowOvertimeConfirmationOpen}
        setIsAllowOvertimeConfirmationOpen={setIsAllowOvertimeConfirmationOpen}
        onSuccess={() => {
          toastService.success(t('dashboard:ALLOW_OVERTIME_SUCCESS'));
          refreshBackgroundPage();
        }}
      />
      <AutoRebidDialog
        orderId={order?.id}
        isAutoRebidConfirmationOpen={isAutoRebidConfirmationOpen}
        setIsAutoRebidConfirmationOpen={setIsAutoRebidConfirmationOpen}
        onSuccess={() => {
          toastService.success(t('success:SHIFT_BIDDING_SUCCESSFULLY_AUTO_EXTENDED'));
          refreshBackgroundPage();
          if (refetch) refetch();
        }}
      />
      {!!order?.shiftBlocks?.nodes?.[0]?.id && (
        <DeleteBlockDialog
          isOpen={isBlockDeleteConfirmationOpen}
          setIsOpen={setIsBlockDeleteConfirmationOpen}
          blockId={order!.shiftBlocks.nodes[0].id}
          onSuccess={() => {
            refreshBackgroundPage();
            shiftModalRoutingService.close();
          }}
        />
      )}
      <CallOutTitleSection>
        <Flex flexDirection="row" flex="1">
          <CallOutHeader>
            <CallOutHeaderWrapper>
              <CallOutHeaderText>{biddingStatusLabel}</CallOutHeaderText>
            </CallOutHeaderWrapper>
          </CallOutHeader>
          {!!order!.currentlyActiveCalloutList?.id && (
            <CallOutViewDetailsLink
              onClick={() => goToAuditTrailView(order!.currentlyActiveCalloutList!.id)}
            >
              {t('shift_modal:VIEW_DETAILS')}
            </CallOutViewDetailsLink>
          )}
        </Flex>
      </CallOutTitleSection>
      <CallOutProgressBarSection>
        <BKJProgressBar value={currentProgress} max={maxStep} height="8px;"></BKJProgressBar>
      </CallOutProgressBarSection>
      <CallOutBodySection>
        <Flex flexDirection="row" flex="1">
          <Flex>
            <AutoExtendLabel>{t('shift_modal:AUTO_EXTEND_LABEL')}:&nbsp;</AutoExtendLabel>
            <AutoExtendValue>
              {order?.autoRebid
                ? t('shift_modal:AUTO_EXTEND_ON')
                : t('shift_modal:AUTO_EXTEND_OFF')}
            </AutoExtendValue>
          </Flex>
          {order!.biddingTimeEndsAt && (
            <EstimatedTimeSection>
              <ProgressBold>{t('shift_modal:BIDDING_ENDS')}:&nbsp;</ProgressBold>
              <ProgressText>
                {DateTime.fromISO(order!.biddingTimeEndsAt)
                  .setZone(timeZone)
                  .toFormat(`EEE, MMM d 'at' ${timeFormat}`)}
              </ProgressText>
            </EstimatedTimeSection>
          )}
        </Flex>
        {isShiftInfoWrapperVisible && (
          <ShiftActionsWrapper
            padding="24px 0 8px 0"
            justifyContent="end"
            onClick={(e) => e.stopPropagation()}
            width="180px"
            margin="0 0 0 auto"
          >
            <UserGuard
              userType={[
                UserPermissionEnum.USER_TYPE_B2B,
                UserPermissionEnum.USER_TYPE_PREMIUM_PRIMARY,
                UserPermissionEnum.USER_TYPE_PREMIUM_SECONDARY,
              ]}
              NOT={['USER_TYPE_READ_ONLY']}
            >
              {!!shiftActions.length && (
                <RenderShiftActions
                  variant="PurpleSolid"
                  shiftActions={shiftActions}
                  SingleItemComponent={ShiftActionComponent}
                />
              )}
            </UserGuard>
          </ShiftActionsWrapper>
        )}
      </CallOutBodySection>
    </BiddingWrapper>
  );
}
